import { useCallback } from 'react';
import type { ApolloQueryResult } from 'apollo-client';

import { mergeChildren } from './data-extractors.ts';
import { PageTreeChildrenQuery } from './queries/PageTreeChildrenQuery.graphql.ts';
import { CHILDREN_PAGE_SIZE } from './paginationLimits.ts';
import { usePageTreeState } from './usePageTreeState.ts';
import { useImperativeQuery } from './useImperativeQuery.ts';
import type { GraphQLPageStatus } from './pageTreeStatuses.ts';
import type { PageTreeChildrenQuery as PageTreeChildrenQueryType } from './queries/__types__/PageTreeChildrenQuery.ts';

type UsePageTreeChildren = ({
	statuses,
	isSuperAdmin,
}: {
	statuses?: GraphQLPageStatus[];
	isSuperAdmin?: boolean;
}) => {
	queryChildren: (
		pageId: any,
		after: string,
		allChildren: ReturnType<typeof mergeChildren>,
	) => Promise<ReturnType<typeof mergeChildren> | void>;
	reloadChildren: () => void;
};

export const usePageTreeChildren: UsePageTreeChildren = ({ statuses, isSuperAdmin }) => {
	const [state, actions] = usePageTreeState();

	const getPageChildren = useImperativeQuery<any, any>(PageTreeChildrenQuery);

	const queryChildren = useCallback(
		(
			pageId: any,
			after: any = undefined,
			allChildren: any = { count: 0, nodes: [] },
			iteration: number = 0,
		) => {
			return getPageChildren({
				contentId: pageId,
				statuses,
				first: CHILDREN_PAGE_SIZE,
				after,
				isSuperAdmin: isSuperAdmin || false,
			})
				.then((response: ApolloQueryResult<PageTreeChildrenQueryType>) => {
					if (response.data) {
						const children = response.data?.ptpage?.children;
						const mergedChildren = children ? mergeChildren(allChildren, children) : { nodes: [] };
						return children?.pageInfo?.hasNextPage &&
							after !== children?.pageInfo?.endCursor && // ensure the new cursor is different
							iteration < 100 // prevent a potential infinite loop
							? queryChildren(pageId, children?.pageInfo?.endCursor, mergedChildren, iteration + 1)
							: { ...mergedChildren, error: null };
					}
					return { nodes: [], error: null };
				})
				.catch((error) => {
					actions.handleLoadingError(error, 'children');
					return { nodes: [], error };
				});
		},
		[getPageChildren, isSuperAdmin, statuses, actions],
	);

	const reloadChildren = useCallback(() => {
		// @ts-ignore
		void queryChildren(state.focusedPageId);
		// @ts-ignore
	}, [queryChildren, state.focusedPageId]);

	return { queryChildren, reloadChildren };
};
